import {
    DeleteOutlined,
    LoadingOutlined,
    PlusCircleOutlined,
} from "@ant-design/icons";
import { Flex, Spin } from "antd";
import React, { type FC, type KeyboardEvent, useCallback } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { getAddressListComponents } from "tm-shared/api";
import { arrayToRecord } from "tm-shared/utils";

import { useAsyncOptions } from "@/hooks";
import { RHFSelect } from "@/shared/forms/RHF/RHFSelect";
import { RHFTextField } from "@/shared/forms/RHF/RHFTextField";
import { InfoCard } from "@/shared/ui/InfoCard";
import type { NewAddressFormValues } from "@/types/form";

import { StyledButton, StyledFieldContainer } from "./Styled";

interface HouseNumberConstructorProps {
    onBack: () => void;
}

export const HouseNumberConstructor: FC<HouseNumberConstructorProps> = ({
    onBack,
}) => {
    const { control, setValue } = useFormContext<NewAddressFormValues>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: "handleComponents",
    });

    const { options, loading, data } = useAsyncOptions({
        fetchFn: () => getAddressListComponents(),
        selectData: (response) => response.result,
        mapToOption: (item) => ({ label: item.label, value: item.type }),
        dependencies: [],
    });

    const typesMap = data?.result ? arrayToRecord(data?.result, "type") : {};

    const handleAddComponent = () => {
        const firstOption = options[0];

        if (!firstOption) return;

        append({ type: firstOption.value, value: "" });
    };

    const watchedFields =
        useWatch<NewAddressFormValues, "handleComponents">({
            name: "handleComponents",
            control,
            defaultValue: [],
        }) ?? [];

    const streetLabel = useWatch<NewAddressFormValues, "streetLabel">({
        name: "streetLabel",
        control,
    });
    const handleNumber = useWatch<NewAddressFormValues, "handleNumber">({
        name: "handleNumber",
        control,
    });

    const lastField =
        watchedFields && watchedFields.length > 0
            ? watchedFields[watchedFields.length - 1]
            : null;

    const lastFieldMeta = lastField ? typesMap[lastField.type] : null;

    const lastNeedsValue = !!lastFieldMeta?.needValue;
    const lastValue = (lastField?.value ?? "").toString().trim();
    const isLastValueEmpty = lastNeedsValue && lastValue.length === 0;

    const isLastElement = !!lastFieldMeta?.isFinish;

    const isAddDisabled = watchedFields.length > 0 && isLastValueEmpty;

    const handlePrefixChange = (index: number) => {
        setValue(`handleComponents.${index}.value`, "", {
            shouldDirty: true,
            shouldValidate: true,
        });

        if (index < fields.length - 1) {
            const indexesToRemove = fields
                .map((_, index_) => index_)
                .filter((index_) => index_ > index);

            remove(indexesToRemove);
        }
    };

    const handleBack = useCallback(() => {
        onBack();
        setValue(`handleComponents`, []);
    }, [onBack, setValue]);

    const addressPreview = watchedFields
        .map(({ value, type }) => `${typesMap[type]?.terminator} ${value}`)
        .join("");

    const handleOnKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === " ") {
                event.preventDefault();
            }
        },
        [],
    );

    const handleValidateInput = useCallback((value: string) => {
        if (!value) return true;

        const onlyLettersAndDigits = /^[0-9A-Za-zА-ЩЬЮЯЇІЄҐа-щьюяїієґ]+$/.test(
            value,
        );

        return onlyLettersAndDigits;
    }, []);

    return loading ? (
        <Flex justify={"center"} align={"center"}>
            <Spin indicator={<LoadingOutlined spin />} size="large" />
        </Flex>
    ) : (
        <>
            <Flex justify={"space-between"} gap={16} align={"center"}>
                <Flex gap={8} wrap={"wrap"} align="center">
                    <StyledFieldContainer>
                        <RHFTextField
                            name="handleNumber"
                            rules={{
                                required: true,
                                validate: (value) => /^\d+$/.test(value),
                            }}
                            pattern="[0-9]*"
                            type="text"
                            inputMode="numeric"
                            onKeyDown={handleOnKeyDown}
                            size="large"
                            formItemProps={{
                                vertical: false,
                            }}
                        />
                    </StyledFieldContainer>

                    {fields.length > 0 &&
                        fields.map((field, index) => {
                            const watchedField = watchedFields[index];
                            const type = watchedField?.type ?? field.type;
                            const meta = typesMap[type];

                            return (
                                <React.Fragment key={field.id}>
                                    <StyledFieldContainer>
                                        <RHFSelect
                                            name={`handleComponents.${index}.type`}
                                            options={options}
                                            onAfterChange={() =>
                                                handlePrefixChange(index)
                                            }
                                            selectProps={{ size: "large" }}
                                        />
                                    </StyledFieldContainer>

                                    {meta?.needValue && (
                                        <StyledFieldContainer>
                                            <RHFTextField
                                                name={`handleComponents.${index}.value`}
                                                onKeyDown={handleOnKeyDown}
                                                rules={{
                                                    required:
                                                        meta.isRequiredValue,
                                                    validate:
                                                        handleValidateInput,
                                                }}
                                                size="large"
                                            />
                                        </StyledFieldContainer>
                                    )}
                                </React.Fragment>
                            );
                        })}

                    {!isLastElement && (
                        <StyledButton
                            onClick={handleAddComponent}
                            $action="add"
                            disabled={isAddDisabled}
                            variant="outlined"
                            type="text"
                            icon={<PlusCircleOutlined />}
                        />
                    )}
                </Flex>

                <StyledButton
                    $action="delete"
                    onClick={handleBack}
                    icon={<DeleteOutlined />}
                    type="text"
                />
            </Flex>

            <InfoCard status="success">{`${streetLabel}, ${handleNumber} ${addressPreview}`}</InfoCard>
        </>
    );
};
